import React, { useState } from 'react'
import { Clock, Video } from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { meetingService } from '@/services/meetingService'
import { useToast } from '@/contexts/ToastContext'
import { MeetingRequest, ProposedTimeSlot } from '@/types/meeting'

const MEETING_TYPE_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  regular_checkin: 'Regular Check-in',
  project_review: 'Project Review',
  career_guidance: 'Career Guidance',
  skill_assessment: 'Skill Assessment',
  goal_setting: 'Goal Setting',
  feedback_session: 'Feedback Session',
  other: 'Other'
}

interface MeetingInviteCardProps {
  request: MeetingRequest
  onResponded: () => void
}

const formatSlot = (slot: ProposedTimeSlot): string => {
  const start = new Date(slot.start)
  const end = new Date(slot.end)
  const day = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  const time = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${day}, ${time(start)} – ${time(end)}`
}

const MeetingInviteCard: React.FC<MeetingInviteCardProps> = ({ request, onResponded }) => {
  const { showToast } = useToast()
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleAccept = async () => {
    if (selectedIndex === null) return
    try {
      setSubmitting(true)
      await meetingService.acceptMeetingRequest(request.id, request.proposedTimes[selectedIndex])
      showToast({ type: 'success', message: 'Meeting confirmed' })
      onResponded()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to accept meeting request'
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleReject = async () => {
    try {
      setSubmitting(true)
      await meetingService.rejectMeetingRequest(request.id, reason.trim() || undefined)
      showToast({ type: 'success', message: 'Meeting request rejected' })
      onResponded()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to reject meeting request'
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card className="mb-4">
      <CardBody>
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-gray-900">
            {MEETING_TYPE_LABELS[request.type] || request.type}
          </h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            Awaiting your response
          </span>
        </div>
        {request.message && <p className="text-sm text-gray-600 mb-2">{request.message}</p>}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
          <span className="flex items-center gap-1">
            <Video className="w-4 h-4" />
            {request.platform}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {request.duration} min
          </span>
        </div>

        <fieldset className="mb-4">
          <legend className="text-sm font-medium text-gray-700 mb-2">Choose a time</legend>
          <div className="space-y-2">
            {request.proposedTimes.map((slot, index) => (
              <label
                key={slot.start}
                className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="radio"
                  name={`slot-${request.id}`}
                  checked={selectedIndex === index}
                  onChange={() => setSelectedIndex(index)}
                  className="text-primary-600"
                />
                <span className="text-sm text-gray-800">{formatSlot(slot)}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {rejecting ? (
          <div className="space-y-2">
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={2}
              className="w-full border rounded-lg p-2 text-sm"
            />
            <div className="flex gap-2">
              <Button variant="danger" onClick={handleReject} disabled={submitting}>
                Confirm rejection
              </Button>
              <Button variant="ghost" onClick={() => setRejecting(false)} disabled={submitting}>
                Back
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={handleAccept}
              disabled={selectedIndex === null || submitting}
            >
              Accept
            </Button>
            <Button variant="outline" onClick={() => setRejecting(true)} disabled={submitting}>
              Reject
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default MeetingInviteCard
